import React, { useContext, useEffect, useRef, useState } from 'react';
import { Container, Navbar, Offcanvas, Spinner } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { faRefresh, faSignOutAlt, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Constants } from '../Models/Constants';
import { EntityColors } from '../Themes/EntityColors';
import { Zigzag } from '../Themes/Brand';
import { UserContext, SiteContext, ToastContext } from '../Contexts';
import { Utils } from '../Services/Utils';
import { UserService } from '../Services/UserService';
import SideMenu from './SideMenu';
import { ChangesContext } from '../Contexts';
import { FrontendEntityChangeResponse, ToastSuccessMessage } from '../Models/Models';
import CustomTooltip from './Tooltips/CustomTooltip';
import { FrontendEntityChangeService } from '../Services/FrontendEntityChangeService';
import FrontendEntityChangesModal from './Modals/FrontendEntityChangeModal';


const NAVBAR_BG = 'var(--st-ink)';
const SIDEBAR_WIDTH_CSS = 'var(--sidebar-width)';
const NAVBAR_HEIGHT_CSS = 'var(--navbar-height)';

const NavigationComponent: React.FC = () => {
    const { logout } = UserService();
    const { getChanges, syncChanges } = FrontendEntityChangeService();
    const location = useLocation();
    const navigate = useNavigate();

    const { currentUser, setCurrentUser }: any = useContext(UserContext);
    const { currentSite, setCurrentSite }: any = useContext(SiteContext);
    const { hasChanges, setHasChanges } = useContext(ChangesContext);
    const [isSyncing, setIsSyncing] = useState(false);
    const { setToast }: any = useContext(ToastContext);

    const [showEnd, setShowEnd] = useState(false);
    const [showStart, setShowStart] = useState(false);

    const [showChangesModal, setShowChangesModal] = useState(false);
    const [changes, setChanges] = useState<FrontendEntityChangeResponse[]>([]);
    const [changesTotal, setChangesTotal] = useState(0);

    useEffect(() => {
        setShowEnd(false);
        setShowStart(false);
    }, [location.pathname]);

    const hideNavPaths = ['/', '/forgottenpassword', '/passwordreset'];
    if (hideNavPaths.includes(location.pathname)) return null;

    const logOut = async () => {
        try {
            await logout();
            setCurrentUser(Constants.defaultUserContext);
            setCurrentSite(Constants.defaultSiteContext);
            setHasChanges(false);
            localStorage.removeItem('tabName');
            navigate('/', { replace: true });
        } catch {
            alert('Network error');
        }
    };

    const handleSync = async () => {
        const res = await getChanges(currentSite.id);
        setChanges(res.data ?? []);
        setChangesTotal(res.totalCount ?? (res.data?.length ?? 0));
        setShowChangesModal(true);
    };

    const confirmSync = async () => {
        setIsSyncing(true);
        try {
            await syncChanges(currentSite.id);
            setHasChanges(false);
            setToast({ message: ToastSuccessMessage.synced });
            setShowChangesModal(false);
        } finally {
            setIsSyncing(false);
        }
    };

    const { usesPost = false, usesGallery = false } = currentSite.settings || {};

    const noWebsite = !currentUser?.websites?.length;

    const filteredNavItems = [
        { entity: 'home', path: '/home', label: 'Domov' },
        { entity: 'blog', path: '/post/table', label: 'Príspevky', show: usesPost },
        { entity: 'gallery', path: '/gallery/table', label: 'Galérie', show: usesGallery },
        {
            entity: 'page', path: '/page/table', label: 'Stránky', show: !noWebsite,
            subItems: [
                { entity: 'text', path: '/text/table', label: 'Texty' },
                { entity: 'image', path: '/pageimage/table', label: 'Obrázky stránok' },
            ],
        },
        { entity: 'list', path: '/list/table', label: 'Zoznamy', show: !noWebsite },
        { entity: 'language', path: '/language/table', label: 'Jazyky', show: !noWebsite },
    { entity: 'image', path: '/image/table', label: 'Obrázky', show: !noWebsite },
        { entity: 'file', path: '/file/table', label: 'Súbory', show: !noWebsite },
        { entity: 'user', path: '/user/table', label: 'Používatelia', show: !!currentUser?.isSuperAdmin },
        { entity: 'website', path: '/website/table', label: 'Weby', show: !!currentUser?.isSuperAdmin },
    ].filter(({ show }) => show !== false) as Array<{
        entity: keyof typeof EntityColors;
        path: string;
        label: string;
        subItems?: Array<{ entity: keyof typeof EntityColors; path: string; label: string }>;
    }>;

    return (
        <>
            {/* ── Top navbar ───────────────────────────────────────────── */}
            <Navbar
                expand={false}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: NAVBAR_BG,
                    borderBottom: '1px solid rgba(255,255,255,0.07)',
                    zIndex: 1040,
                    height: NAVBAR_HEIGHT_CSS,
                }}
            >
                <Container fluid className="d-flex align-items-center justify-content-between px-3">

                    {/* Mobile: site picker toggle */}
                    {Utils.isUserLoggedIn(currentUser) && (
                        <button
                            onClick={() => setShowStart(v => !v)}
                            className="d-md-none"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.4rem',
                                padding: '0.35rem 0.7rem',
                                borderRadius: '8px',
                                border: '1px solid rgba(255,255,255,0.14)',
                                backgroundColor: 'rgba(255,255,255,0.07)',
                                color: '#CFC7E4',
                                fontFamily: 'var(--st-font-display)',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            <FontAwesomeIcon icon={faGlobe} style={{ fontSize: '0.7rem' }} />
                            {Utils.capitalizeFirstLetter(currentSite.shortName) || 'Stránka'}
                        </button>
                    )}



                    <div style={{ flex: 1 }} />

                    {/* Sync button */}
                    <CustomTooltip placement="bottom" hideOn={isSyncing || hasChanges} message="Žiadne zmeny na uloženie">
                        <span className="d-inline-block me-2">
                            <button
                                disabled={!hasChanges || isSyncing}
                                onClick={handleSync}
                                className={hasChanges && !isSyncing ? 'sync-pulse' : undefined}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.45rem',
                                    padding: '0.4rem 0.9rem',
                                    borderRadius: '8px',
                                    border: hasChanges
                                        ? 'none'
                                        : '1px solid rgba(255,255,255,0.13)',
                                    backgroundColor: hasChanges ? 'var(--st-accent)' : 'rgba(255,255,255,0.06)',
                                    color: hasChanges ? 'white' : 'rgba(207,199,228,0.5)',
                                    fontFamily: 'var(--st-font-display)',
                                    fontWeight: 600,
                                    fontSize: '0.82rem',
                                    cursor: hasChanges && !isSyncing ? 'pointer' : 'default',
                                    transition: 'background-color 0.15s',
                                }}
                            >
                                {isSyncing ? (
                                    <>Synchronizujem <Spinner size="sm" /></>
                                ) : (
                                    <>Synchronizovať <FontAwesomeIcon icon={faRefresh} style={{ fontSize: '0.75rem' }} /></>
                                )}
                            </button>
                        </span>
                    </CustomTooltip>

                    {/* Nav drawer toggle / login */}
                    {Utils.isUserLoggedIn(currentUser) ? (
                        <button
                            onClick={() => setShowEnd(v => !v)}
                            title="Navigácia"
                            style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '8px',
                                border: '1px solid rgba(255,255,255,0.14)',
                                backgroundColor: 'rgba(255,255,255,0.07)',
                                color: '#CFC7E4',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <HamburgerIcon />
                        </button>
                    ) : (
                        <Link to="/">
                            <button style={{
                                padding: '0.4rem 0.95rem',
                                borderRadius: '8px',
                                border: 'none',
                                backgroundColor: 'var(--st-accent)',
                                color: 'white',
                                fontFamily: 'var(--st-font-display)',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                cursor: 'pointer',
                            }}>
                                Prihlásiť sa
                            </button>
                        </Link>
                    )}
                </Container>
            </Navbar>

            {/* ── Changes modal ────────────────────────────────────────── */}
            <FrontendEntityChangesModal
                show={showChangesModal}
                onHide={() => setShowChangesModal(false)}
                changes={changes}
                totalCount={changesTotal}
                isConfirming={isSyncing}
                onConfirm={confirmSync}
            />

            {/* ── Mobile: site picker offcanvas (left) ─────────────────── */}
            <Offcanvas
                show={showStart}
                onHide={() => setShowStart(false)}
                placement="start"
                className="d-md-none"
                style={{ width: SIDEBAR_WIDTH_CSS, backgroundColor: NAVBAR_BG }}
            >
                <Offcanvas.Header
                    closeButton
                    closeVariant="white"
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '0.75rem' }}
                >
                    <span className="st-eyebrow" style={{ color: 'rgba(207,199,228,0.55)' }}>
                        Weby
                    </span>
                </Offcanvas.Header>
                <Offcanvas.Body className="p-0">
                    <SideMenu />
                </Offcanvas.Body>
            </Offcanvas>

            {/* ── Right nav drawer ─────────────────────────────────────── */}
            <Offcanvas
                show={showEnd}
                onHide={() => setShowEnd(false)}
                placement="end"
                style={{ width: '250px', backgroundColor: NAVBAR_BG }}
            >
                <Offcanvas.Header
                    closeButton
                    closeVariant="white"
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                >
                    <span className="st-eyebrow" style={{ color: 'rgba(207,199,228,0.55)' }}>
                        Navigácia
                    </span>
                </Offcanvas.Header>
                <Offcanvas.Body className="d-flex flex-column p-0">
                    <div style={{ padding: '0.6rem 0.6rem', flex: 1, overflowY: 'auto' }}>
                        {filteredNavItems.map(({ entity, path, label, subItems }) => {
                            const { icon, action } = EntityColors[entity];
                            const isActive = location.pathname === path
                                || (path !== '/home' && location.pathname.startsWith(`/${entity}`));

                            return (
                                <NavDrawerItem
                                    key={entity}
                                    to={path}
                                    label={label}
                                    icon={icon}
                                    accentColor={action}
                                    isActive={isActive}
                                    onClick={() => setShowEnd(false)}
                                    subItems={subItems?.map(sub => {
                                        const subColors = EntityColors[sub.entity];
                                        return {
                                            to: sub.path,
                                            label: sub.label,
                                            icon: subColors.icon,
                                            accentColor: subColors.action,
                                            isActive: location.pathname.startsWith(`/${sub.entity}`),
                                        };
                                    })}
                                />
                            );
                        })}
                    </div>

                    <div style={{ padding: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        <LogoutButton onClick={logOut} />
                    </div>
                </Offcanvas.Body>
            </Offcanvas>

            {/* ── Desktop fixed sidebar ────────────────────────────────── */}
            <div
                className="d-none d-md-block"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: SIDEBAR_WIDTH_CSS,
                    height: '100vh',
                    overflowY: 'auto',
                    backgroundColor: NAVBAR_BG,
                    borderRight: '1px solid rgba(255,255,255,0.07)',
                    zIndex: 1030,
                }}
            >
                {/* Spacer matching navbar height + mark */}
                <div style={{
                    height: NAVBAR_HEIGHT_CSS,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.3rem',
                    borderBottom: '1px solid rgba(255,255,255,0.07)',
                    flexShrink: 0,
                }}>
                    <Zigzag color="#8B77E8" width={22} height={7} strokeWidth={1.6} />
                    <span className="st-eyebrow" style={{ fontSize: '0.5rem', color: 'rgba(207,199,228,0.45)' }}>
                        Stabilimentum
                    </span>
                </div>
                <SideMenu />
            </div>
        </>
    );
};

/* ── Small reusable pieces ───────────────────────────────────────────── */

const HamburgerIcon: React.FC = () => (
    <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
        <rect width="16" height="2" rx="1" fill="currentColor" />
        <rect y="5" width="12" height="2" rx="1" fill="currentColor" />
        <rect y="10" width="8" height="2" rx="1" fill="currentColor" />
    </svg>
);

interface SubNavItem {
    to: string;
    label: string;
    icon: any;
    accentColor: string;
    isActive: boolean;
}

const NavDrawerItem: React.FC<{
    to: string;
    label: string;
    icon: any;
    accentColor: string;
    isActive: boolean;
    onClick: () => void;
    subItems?: SubNavItem[];
}> = ({ to, label, icon, accentColor, isActive, onClick, subItems }) => {
    const [hovered, setHovered] = useState(false);
    const [groupHovered, setGroupHovered] = useState(false);
    const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const hasSub = !!subItems?.length;
    const subActive = subItems?.some(s => s.isActive) ?? false;
    // Stays open while the pointer is anywhere in the group, or when a child route is active
    const open = groupHovered || subActive;

    useEffect(() => () => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    }, []);

    const handleGroupMouseEnter = () => {
        if (!hasSub) return;
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        // Delay opening so a cursor passing through on its way elsewhere doesn't
        // trigger the layout shift before the user has actually paused here.
        hoverTimeoutRef.current = setTimeout(() => setGroupHovered(true), 300);
    };

    const handleGroupMouseLeave = () => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        setGroupHovered(false);
    };

    return (
        <div
            onMouseEnter={handleGroupMouseEnter}
            onMouseLeave={handleGroupMouseLeave}
        >
            <Link
                to={to}
                onClick={onClick}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.65rem',
                    padding: '0.55rem 0.75rem',
                    borderRadius: '8px',
                    marginBottom: '0.15rem',
                    textDecoration: 'none',
                    backgroundColor: isActive
                        ? 'rgba(255,255,255,0.08)'
                        : hovered
                        ? 'rgba(255,255,255,0.04)'
                        : 'transparent',
                    borderLeft: isActive ? `3px solid ${accentColor}` : '3px solid transparent',
                    transition: 'background-color 0.15s',
                }}
            >
                <FontAwesomeIcon
                    icon={icon}
                    style={{ color: isActive ? accentColor : 'rgba(207,199,228,0.4)', fontSize: '0.85rem', width: '14px', flexShrink: 0 }}
                />
                <span style={{
                    color: isActive ? '#F4F4F5' : hovered ? '#CFC7E4' : 'rgba(207,199,228,0.55)',
                    fontSize: '0.875rem',
                    fontWeight: isActive ? 600 : 400,
                }}>
                    {label}
                </span>
            </Link>

            {hasSub && (
                <div className={`st-subnav${open ? ' st-subnav--open' : ''}`}>
                    <div className="st-subnav__inner">
                        <div style={{
                            margin: '0 0 0.25rem 1.15rem',
                            paddingLeft: '0.5rem',
                            borderLeft: '1px solid rgba(207,199,228,0.18)',
                        }}>
                            {subItems!.map(sub => (
                                <SubNavLink key={sub.to} {...sub} onClick={onClick} />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const SubNavLink: React.FC<SubNavItem & { onClick: () => void }> = ({ to, label, icon, accentColor, isActive, onClick }) => {
    const [hovered, setHovered] = useState(false);

    return (
        <Link
            to={to}
            onClick={onClick}
            className="st-subnav__item"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.55rem',
                padding: '0.4rem 0.6rem',
                borderRadius: '7px',
                textDecoration: 'none',
                backgroundColor: isActive
                    ? 'rgba(255,255,255,0.08)'
                    : hovered
                    ? 'rgba(255,255,255,0.04)'
                    : 'transparent',
            }}
        >
            <FontAwesomeIcon
                icon={icon}
                style={{ color: isActive ? accentColor : 'rgba(207,199,228,0.4)', fontSize: '0.72rem', width: '12px', flexShrink: 0 }}
            />
            <span style={{
                color: isActive ? '#F4F4F5' : hovered ? '#CFC7E4' : 'rgba(207,199,228,0.55)',
                fontSize: '0.8rem',
                fontWeight: isActive ? 600 : 400,
            }}>
                {label}
            </span>
        </Link>
    );
};

const LogoutButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    const [hovered, setHovered] = useState(false);

    return (
        <button
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                padding: '0.5rem',
                borderRadius: '8px',
                border: '1px solid rgba(230,106,128,0.4)',
                backgroundColor: hovered ? 'rgba(206,58,86,0.16)' : 'transparent',
                color: '#E67F92',
                fontFamily: 'var(--st-font-display)',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'background-color 0.15s',
            }}
        >
            <FontAwesomeIcon icon={faSignOutAlt} style={{ fontSize: '0.8rem' }} />
            Odhlásiť sa
        </button>
    );
};

export default NavigationComponent;
